import { DetailedLoanCard } from "@/components/DetailedLoanCard";
import { useTrustpilotRating } from "@/hooks/useTrustpilotRatings";
import { useProviderLiveTerms, type ProviderEntityType, type ProviderLiveTerms } from "@/hooks/useProviderLiveTerms";
import {
  getLiveAmountLabel,
  getLiveLenderBadge,
  getLiveLoanWithoutUCLabel,
  getLiveTermLabel,
  getPreferredRateLabel,
  getUsableLiveTerms,
} from "@/utils/providerLiveTerms";

interface DynamicDetailedLoanCardProps {
  provider: string;
  logo: string;
  loanAmount: string;
  nominalRate: string;
  effectiveRate: string;
  loanTerm: string;
  loanWithoutUC: string;
  responseTime: string;
  paymentSolution: string;
  link: string;
  lenderBadge?: string;
  // Trustpilot data
  entityType: ProviderEntityType;
  entitySlug: string;
  fallbackRating: number;
  fallbackReviewCount: number;
  liveTermsOverride?: ProviderLiveTerms | null;
}

/**
 * A wrapper around DetailedLoanCard that fetches dynamic ratings and live terms.
 */
export function DynamicDetailedLoanCard({
  provider,
  logo,
  loanAmount,
  nominalRate,
  effectiveRate,
  loanTerm,
  loanWithoutUC,
  responseTime,
  paymentSolution,
  link,
  lenderBadge,
  entityType,
  entitySlug,
  fallbackRating,
  fallbackReviewCount,
  liveTermsOverride,
}: DynamicDetailedLoanCardProps) {
  const { data: trustpilotData } = useTrustpilotRating(entityType, entitySlug);
  const shouldFetchLiveTerms = liveTermsOverride === undefined;
  const { data: fetchedLiveTerms } = useProviderLiveTerms(entityType, entitySlug, { enabled: shouldFetchLiveTerms });
  const liveTerms = shouldFetchLiveTerms ? fetchedLiveTerms : liveTermsOverride;
  const usableLiveTerms = getUsableLiveTerms(liveTerms);
  const preferredRate = getPreferredRateLabel(usableLiveTerms);

  return (
    <DetailedLoanCard
      provider={provider}
      logo={logo}
      loanAmount={getLiveAmountLabel(usableLiveTerms, loanAmount)}
      nominalRate={usableLiveTerms?.nominal_rate_label || nominalRate}
      effectiveRate={preferredRate || effectiveRate}
      loanTerm={getLiveTermLabel(usableLiveTerms, loanTerm)}
      loanWithoutUC={getLiveLoanWithoutUCLabel(usableLiveTerms, loanWithoutUC)}
      responseTime={usableLiveTerms?.payout_time_label || responseTime}
      paymentSolution={paymentSolution}
      link={link}
      rating={trustpilotData?.rating ?? fallbackRating}
      reviewCount={trustpilotData?.reviewCount ?? fallbackReviewCount}
      lenderBadge={getLiveLenderBadge(usableLiveTerms, lenderBadge)}
    />
  );
}
